import { onBeforeUnmount, onMounted } from 'vue'
import { storeToRefs } from 'pinia'
import { useSessionStore } from '../stores/session'
import { useSourceActivityStore } from '../stores/source_activity'
import { useThreadsStore } from '../stores/threads'
import { useLintNotesStore } from '../stores/lint_notes'
import { useSynthesisSuggestionsStore } from '../stores/synthesis_suggestions'
import { useAgentStore } from '../stores/agent'
import { useFailedJobsStore } from '../stores/failed_jobs'
import { useAgentHealthStore } from '../stores/agent_health'

// Declarative registry of admin stores that need global subscriptions.
//
// Adding a new admin feature whose chrome lives in the header or
// command palette (badge, dot, count) = add subscribe() to the store
// and one entry to the list below; no view edits required.
//
// Structure of each entry:
//   subscribe() — wires the live ActionCable channel. All admin stores
//     expose subscribe() (threads bootstraps its one-shot recent load here).
//   load() — optional initial data fetch fired on admin mount alongside
//     subscribe(). Stores whose subscribe() already triggers a fetch omit it.
//   unsubscribe() — teardown hook; source_activity holds a channel that
//     needs release on sign-out, and failed_jobs holds a poll interval
//     that needs the same treatment (see below).
//
// Agent doesn't fit the uniform subscribe() interface — it has a
// per-thread subscribe(threadId) that collides with a no-arg admin
// subscription — so it is handled via its explicit subscribeStatus() +
// fetchSessions() pair, listed as a normal entry.

interface BootstrapEntry {
  subscribe(): void
  load?: () => void | Promise<void>
  unsubscribe?: () => void
}

export function useAdminSubscriptions(): void {
  const { currentUser } = storeToRefs(useSessionStore())
  const sourceActivity = useSourceActivityStore()
  const threads = useThreadsStore()
  const lintNotes = useLintNotesStore()
  const synthesisSuggestions = useSynthesisSuggestionsStore()
  const agent = useAgentStore()
  const failedJobs = useFailedJobsStore()
  const agentHealth = useAgentHealthStore()

  const entries: BootstrapEntry[] = [
    // Source activity — drives the ingestion dot + activity drawer.
    {
      subscribe: () => sourceActivity.subscribe(),
      load: () => sourceActivity.load(),
      unsubscribe: () => sourceActivity.unsubscribe(),
    },
    // Threads — populates the command palette's "recent threads" group.
    { subscribe: () => threads.subscribe() },
    // Lint notes — drives the header open-count badge from any route.
    { subscribe: () => lintNotes.subscribe(), load: () => lintNotes.fetchAll() },
    // Synthesis suggestions — same role as lint notes for the synthesis half.
    { subscribe: () => synthesisSuggestions.subscribe(), load: () => synthesisSuggestions.fetchAll() },
    // Agent — running-session dot in the header from any route, plus
    // live session-list metadata (new sessions, renames) so it's fresh
    // even before the user ever opens /agent (#831).
    {
      subscribe: () => {
        agent.subscribeStatus()
        agent.subscribeSessionsList()
      },
      load: () => agent.fetchSessions(),
    },
    // Failed jobs — drives the standing red banner from any route (#1087).
    // No ActionCable channel for Solid Queue failures exists, so the
    // store owns its own poll interval; subscribe()/unsubscribe() here
    // just start/stop it.
    { subscribe: () => failedJobs.subscribe(), unsubscribe: () => failedJobs.unsubscribe() },
    // Agent health — paused/failed product-owner repos as a header badge (#1974); polls like failed jobs.
    { subscribe: () => agentHealth.subscribe(), unsubscribe: () => agentHealth.unsubscribe() },
  ]

  onMounted(() => {
    if (!currentUser.value?.admin) return
    entries.forEach(({ subscribe, load }) => {
      subscribe()
      void load?.()
    })
  })

  onBeforeUnmount(() => {
    entries.forEach((e) => e.unsubscribe?.())
  })
}
